import { Link } from "react-router-dom";
import { Star, MapPin, Share2, Search, ShoppingBag, ArrowUpRight } from "lucide-react";
import { getDeptDisplayLabel } from "@/lib/departementDisplay";
import { cn } from "@/lib/utils";

const sectorAccent = {
  BTP: "border-amber-200 bg-amber-50 text-amber-800",
  Finance: "border-blue-200 bg-blue-50 text-blue-800",
  Assurance: "border-emerald-200 bg-emerald-50 text-emerald-800",
  Tech: "border-violet-200 bg-violet-50 text-violet-800",
  Immobilier: "border-rose-200 bg-rose-50 text-rose-800",
  Énergie: "border-emerald-200 bg-emerald-50 text-emerald-800",
  "Énergie Renouvelable": "border-emerald-200 bg-emerald-50 text-emerald-800",
  "Energie Renouvelable": "border-emerald-200 bg-emerald-50 text-emerald-800",
};

function normalizeKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function extractPostalCode(text) {
  const t = String(text || "").trim();
  if (!t) return null;
  const m = t.match(/\b(\d{4,5})\b/);
  return m ? m[1] : null;
}

function extractCountryCode(dossier) {
  const cf =
    dossier?.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
      ? dossier.custom_fields
      : {};
  for (const k of Object.keys(cf)) {
    const nk = normalizeKey(k);
    if (nk === "pays" || nk === "country") {
      const v = String(cf[k] || "").trim().toUpperCase();
      if (v === "FR" || v === "FRANCE") return "FR";
      if (v === "BE" || v.includes("BELGI")) return "BE";
      if (v === "CH" || v.includes("SUIS")) return "CH";
    }
  }
  return "FR";
}

function getGeoLabel(dossier) {
  const country = extractCountryCode(dossier);
  if (country === "FR") {
    const dept = getDeptDisplayLabel(dossier);
    return dept ? { kind: "dept", value: dept } : null;
  }
  const cf =
    dossier?.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
      ? dossier.custom_fields
      : {};
  for (const k of Object.keys(cf)) {
    const nk = normalizeKey(k);
    if (nk.includes("code postal") || nk.includes("postal") || nk.includes("adresse") || nk.includes("address")) {
      const cp = extractPostalCode(cf[k]);
      if (cp) return { kind: "postal", value: cp };
    }
  }
  const cp = extractPostalCode(dossier?.location);
  return cp ? { kind: "postal", value: cp } : null;
}

function formatPrice(dossier) {
  if (dossier.type === "recherche" && dossier.price_min != null && dossier.price_max != null) {
    return `${Number(dossier.price_min).toLocaleString("fr-FR")} – ${Number(dossier.price_max).toLocaleString("fr-FR")} €`;
  }
  if (dossier.price != null && dossier.price !== "") {
    return `${Number(dossier.price).toLocaleString("fr-FR")} €`;
  }
  return "Prix sur demande";
}

function DossierGeoBadge({ geo, className }) {
  if (!geo?.value) return null;
  const isDept = geo.kind === "dept";
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-bold tracking-tight",
        "bg-[#2D7FF9]/12 text-[#1a5fc4] ring-2 ring-[#2D7FF9]/30",
        className,
      )}
      title={isDept ? `Département ${geo.value}` : geo.value}
    >
      <MapPin className="h-4 w-4 shrink-0 text-[#2D7FF9]" aria-hidden />
      <span className="truncate">{isDept ? geo.value : geo.value}</span>
      {isDept ? (
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#2D7FF9]/80">
          dép.
        </span>
      ) : null}
    </span>
  );
}

const DossierCard = ({ dossier, to, actions = null, compact = false, variant = "grid", onBeforeOpen }) => {
  const isRecherche = dossier.type === "recherche";
  const sectorClass = sectorAccent[dossier.secteur] || "border-slate-200 bg-slate-50 text-slate-700";
  const publicHref = `/dossier/${dossier.dossier_id}`;
  const href = typeof to === "string" ? to : publicHref;
  const shareUrl = `${window.location.origin}${publicHref}`;
  const handleOpenClick = () => {
    onBeforeOpen?.();
  };
  const geo = getGeoLabel(dossier);
  const hasRating = (dossier.seller_rating_count || 0) > 0;
  const isList = variant === "list";

  if (isList) {
    const listClass = cn(
      "group relative flex h-full w-full min-w-0 items-stretch gap-0 overflow-hidden rounded-xl border bg-white text-left",
      "border-slate-200/90 shadow-sm transition-all duration-200",
      "hover:border-[#2D7FF9]/40 hover:shadow-md",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/50",
    );
    const listBody = (
      <>
        <div
          className={cn(
            "flex w-12 shrink-0 flex-col items-center justify-center gap-1 sm:w-14",
            isRecherche ? "bg-sky-50/90" : "bg-slate-50/90",
          )}
        >
          <span
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              isRecherche ? "bg-[#2D7FF9] text-white" : "bg-white text-slate-700 ring-1 ring-slate-200/80",
            )}
            aria-hidden
          >
            {isRecherche ? <Search className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:py-3.5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {isRecherche ? "Recherche" : "Offre"}
              </span>
              {dossier.secteur ? (
                <span
                  className={cn(
                    "inline-flex max-w-[10rem] truncate rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                    sectorClass,
                  )}
                  title={dossier.secteur}
                >
                  {dossier.secteur}
                </span>
              ) : null}
            </div>
            <h3 className="mt-0.5 font-display text-base font-bold leading-snug text-slate-900 line-clamp-2 transition-colors group-hover:text-[#2D7FF9] sm:text-lg">
              {dossier.title}
            </h3>
            {dossier.description ? (
              <p className="mt-0.5 hidden text-sm text-slate-600 line-clamp-1 sm:block">{dossier.description}</p>
            ) : null}
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {geo?.value ? <DossierGeoBadge geo={geo} className="text-xs sm:text-sm" /> : null}
              {hasRating ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-950 ring-1 ring-amber-200/80">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" aria-hidden />
                  {(dossier.seller_rating || 0).toFixed(1)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end sm:gap-1">
            <p className="font-display text-xl font-black tabular-nums tracking-tight text-[#0B0D12] sm:text-2xl">
              {formatPrice(dossier)}
            </p>
            <span className="hidden items-center gap-0.5 text-xs font-semibold text-[#2D7FF9] sm:flex">
              Voir
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </span>
          </div>
        </div>
        <button
          type="button"
          className="flex w-10 shrink-0 items-center justify-center border-l border-slate-100 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"
          aria-label="Partager l'annonce"
          title="Partager"
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              if (navigator.share) {
                await navigator.share({ url: shareUrl });
              } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
              }
            } catch {
              /* annulé */
            }
          }}
        >
          <Share2 className="h-4 w-4" aria-hidden />
        </button>
      </>
    );
    if (actions) {
      return (
        <article className={listClass} data-testid={`dossier-card-${dossier.dossier_id}`}>
          <Link to={href} className="flex min-w-0 flex-1 items-stretch" onClick={handleOpenClick}>
            {listBody}
          </Link>
          <div className="border-t border-slate-100 px-3 py-2">{actions}</div>
        </article>
      );
    }
    return (
      <Link
        to={href}
        className={listClass}
        data-testid={`dossier-card-${dossier.dossier_id}`}
        onClick={handleOpenClick}
      >
        {listBody}
      </Link>
    );
  }

  const cardClass = cn(
    "group relative flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border bg-white text-left",
    "border-slate-200/90 shadow-sm transition-all duration-200",
    "hover:border-[#2D7FF9]/40 hover:shadow-[0_12px_40px_-12px_rgba(45,127,249,0.25)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/50",
    compact ? "min-h-[220px]" : "min-h-[280px]",
  );

  const headerClass = cn(
    "relative flex items-center justify-between gap-2 px-4 py-3",
    isRecherche ? "bg-sky-50/90" : "bg-slate-50/90",
  );

  const content = (
    <>
      <div className={headerClass}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
              isRecherche ? "bg-[#2D7FF9] text-white" : "bg-white text-slate-700 ring-1 ring-slate-200/80",
            )}
            aria-hidden
          >
            {isRecherche ? <Search className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {isRecherche ? "Recherche acheteur" : "Offre vendeur"}
            </p>
            {dossier.secteur ? (
              <span
                className={cn(
                  "mt-0.5 inline-flex max-w-full truncate rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                  sectorClass,
                )}
                title={dossier.secteur}
              >
                {dossier.secteur}
            </span>
            ) : null}
          </div>
        </div>
          <button
            type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white/80 hover:text-slate-700"
            aria-label="Partager l'annonce"
            title="Partager"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                if (navigator.share) {
                  await navigator.share({ url: shareUrl });
                } else if (navigator.clipboard?.writeText) {
                  await navigator.clipboard.writeText(shareUrl);
                }
              } catch {
              /* annulé */
              }
            }}
          >
          <Share2 className="h-4 w-4" aria-hidden />
          </button>
      </div>

      <div className={cn("flex flex-1 flex-col px-4 pb-4", compact ? "pt-3" : "pt-4")}>
        <p
          className={cn(
            "font-display font-black tabular-nums tracking-tight text-[#0B0D12]",
            compact ? "text-2xl" : "text-[1.75rem] sm:text-3xl",
          )}
        >
          {formatPrice(dossier)}
        </p>

        {geo?.value ? <DossierGeoBadge geo={geo} className="mt-2 w-fit max-w-full" /> : null}

        <h3
          className={cn(
            "mt-2 font-display font-bold leading-snug text-slate-900 line-clamp-2 transition-colors group-hover:text-[#2D7FF9]",
            compact ? "text-sm min-h-[2.5rem]" : "text-base min-h-[2.75rem] sm:text-lg",
          )}
        >
        {dossier.title}
      </h3>

        {dossier.description ? (
          <p
            className={cn(
              "mt-2 flex-1 text-slate-600 line-clamp-2 leading-relaxed",
              compact ? "text-xs" : "text-sm",
            )}
          >
        {dossier.description}
      </p>
        ) : (
          <div className="flex-1" />
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          {hasRating ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-950 ring-1 ring-amber-200/80">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
              {(dossier.seller_rating || 0).toFixed(1)}
              <span className="font-normal text-amber-800/80">({dossier.seller_rating_count})</span>
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80">
              Nouveau vendeur
            </span>
          )}
        </div>

        <p className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#2D7FF9] opacity-0 transition-opacity group-hover:opacity-100">
          Voir l&apos;annonce
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        </p>
      </div>
    </>
  );

  if (actions) {
    return (
      <article className={cardClass} data-testid={`dossier-card-${dossier.dossier_id}`}>
        <Link to={href} className="contents" onClick={handleOpenClick}>
          {content}
        </Link>
        <div className="border-t border-slate-100 px-4 py-3">{actions}</div>
      </article>
    );
  }

  return (
    <Link
      to={href}
      className={cardClass}
      data-testid={`dossier-card-${dossier.dossier_id}`}
      onClick={handleOpenClick}
    >
      {content}
    </Link>
  );
};

export default DossierCard;
